import { Component, ReactNode } from 'react'
import * as cn from 'classnames'
import * as debounce from 'lodash/debounce'
import * as concat from 'lodash/concat'
import PieChart from 'components/atoms/charts/PieChart'
import PieChartPlaceholder from 'components/atoms/charts/PieChartPlaceholder'
import CategoriesList, { CategoryItem } from 'components/molecules/CategoriesList'
import CategoriesListMobile from 'components/molecules/CategoriesListMobile'
import Button from 'components/features/pfm/Button/Button'
import { PieChartSector } from 'interfaces/chart'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { CategoryId } from 'interfaces/meta'
import * as styles from 'components/features/pfm/CategoriesModule/CategoriesModule.css'
import Carousel from 'components/molecules/Carousel'
import { MoneyflowType } from 'interfaces/moneyflow'
import { ALL_CATEGORY_ID } from 'constants/meta'
import { AnalyticsEvent } from 'interfaces/analytics'

// properties for on section of this module (member, peers, other peers)
type CategoriesGroup = {
  total: number
  mainColor: string
  mainFontColor: string
  chart: PieChartSector[]
}

type CategoriesGroupWithList = CategoriesGroup & {
  list: {
    fontColor: string
    items: CategoryItem[]
  }
}

type CategoriesModuleBaseStateProps = {
  isMobile: boolean
  selectedPeergroupName: string | null
  withComparison: boolean
}

type CategoriesModuleWithListStateProps = CategoriesModuleBaseStateProps & {
  type: MoneyflowType
  selectedId: CategoryId
  isAllCategorySelected: boolean
  withList: true
  isExpanded: boolean
  member: CategoriesGroupWithList
  peers: CategoriesGroupWithList
  others: CategoriesGroupWithList
}

type CategoriesModuleWithoutListStateProps = CategoriesModuleBaseStateProps & {
  isMobile?: boolean
  selectedPeergroupName: string | null
  withList: false
  member: CategoriesGroup
  peers: CategoriesGroup
  others: CategoriesGroup
}

export type CategoriesModuleStateProps =
  | CategoriesModuleWithListStateProps
  | CategoriesModuleWithoutListStateProps

export type CategoriesModuleDispatchProps = {
  highlightCategory: (category: CategoryId | null) => void
  setFilteredCategory?: (category: CategoryId | null) => void
  setExpanded?: (isExpanded: boolean) => void
  trackUIEvent: (event: AnalyticsEvent) => void
}

export type CategoriesModuleOwnProps = {
  piePlaceholderLabel: ReactNode
}

export type CategoriesModuleProps =
  & CategoriesModuleStateProps
  & CategoriesModuleDispatchProps
  & CategoriesModuleOwnProps

export type CategoriesModuleState = {
  isShown: boolean
  columnWidthPx: number
  activeIndex: number
  selected: number
}

const PIES_COUNT = 3
const PIES_COUNT_MOBILE = 1
const SHOWN_CATEGORIES_COUNT_MOBILE = 5
const INITIAL_COLUMN_SIZE_PX = parseFloat(styles.initialColumnSize)

class CategoriesModule extends Component<CategoriesModuleProps, CategoriesModuleState> {
  state: CategoriesModuleState = {
    isShown: false,
    columnWidthPx: INITIAL_COLUMN_SIZE_PX,
    activeIndex: 0,
    selected: 0,
  }

  private moduleNode: HTMLDivElement | null = null

  private get isExpanded () {
    return this.props.withList && this.props.isExpanded
  }

  private get isMobileAndSpecificCategorySelected () {
    return this.props.withList && this.props.isMobile && !this.props.isAllCategorySelected
  }

  private get shouldShowButton () {
    const { isShown } = this.state

    return this.props.withList && isShown && this.props.setExpanded
      && !this.isMobileAndSpecificCategorySelected
      && !(this.props.isMobile && this.props.member.list.items.length < SHOWN_CATEGORIES_COUNT_MOBILE)
  }

  static getDerivedStateFromProps (nextProps: CategoriesModuleProps, prevState: CategoriesModuleState) {
    const { withComparison } = nextProps
    const { activeIndex } = prevState
    if (!withComparison && activeIndex !== 0) {
      return {
        ...prevState,
        activeIndex: 0,
        selected: 0,
      }
    }

    return prevState
  }

  render () {
    const {
      selectedPeergroupName,
      isMobile,
    } = this.props
    const { isShown } = this.state

    if (!selectedPeergroupName) {
      return null
    }

    const pies = isShown
      ? this.renderPies()
      : null

    return (
      <div
        className={ styles.categoriesModule }
        ref={ (el) => this.moduleNode = el }
      >
        { pies }
        { isMobile && this.renderListsMobile() }
        { !isMobile && this.isExpanded && this.renderLists() }
        { this.shouldShowButton && (
          <div className={ styles.buttonWrapper }>
            <Button
              fullWidth
              theme='lightBlue'
              size='small'
              onClick={ this.handleButtonClick }
              qaMarker={ this.isExpanded ? 'see-less' : 'see-more' }
            >
              { this.isExpanded ? 'See less' : 'See more detail' }
            </Button>
          </div>
        )}
      </div>
    )

  }

  renderLists () {
    if (!this.props.withList) {
      return null
    }

    const {
      member,
      peers,
      others,
      highlightCategory,
      withComparison,
    } = this.props

    return (
      <div className={ styles.lists }>
        <section className={ cn(styles.listWrapper, !withComparison && styles.noComparison) } data-qa-marker='your'>
          <CategoriesList
            fontColor={ member.list.fontColor }
            items={ member.list.items }
            onHoverItem={ highlightCategory }
            onClickItem={ (categoryId) => this.handleCategoryClick(categoryId, false) }
          />
        </section>
        { withComparison && (
          <section className={ styles.listWrapper } data-qa-marker='peers'>
            <CategoriesList
              fontColor={ peers.list.fontColor }
              items={ peers.list.items }
              onHoverItem={ highlightCategory }
              onClickItem={ (categoryId) => this.handleCategoryClick(categoryId, false) }
            />
          </section>
        )}
        { withComparison && (
          <section className={ styles.listWrapper } data-qa-marker='other'>
            <CategoriesList
              fontColor={ others.list.fontColor }
              items={ others.list.items }
              onHoverItem={ highlightCategory }
              onClickItem={ (categoryId) => this.handleCategoryClick(categoryId, false) }
            />
          </section>
        )}
      </div>
    )
  }

  renderTitle (total: number, title: string, className: string, marker?: string) {
    return (
      <div className={ cn(styles.pieTitle, className) } data-qa-marker={ `pie-chart-title-${ marker }` } >
        <div className={ styles.pieTitleName } data-qa-type='title' >
          { title } total:
        </div>
        <div className={ styles.pieTitleValue } data-qa-type='value'>
          { formatUSCurrency(total) }
        </div>
      </div>
    )
  }

  renderPie (data: CategoriesGroup, marker?: string) {
    const {
      highlightCategory,
      piePlaceholderLabel,
      withComparison,
    } = this.props
    const isEmpty = !data.chart.length || data.chart.every((item) => item.value === 0)

    return (
      <section className={ styles.pieSection } data-qa-marker={ marker }>
        <section className={ cn(styles.pieWrapper, !withComparison && styles.noComparison) }>
          { isEmpty ? (
            <PieChartPlaceholder color={ data.mainColor } size={ this.state.columnWidthPx }>
              { piePlaceholderLabel }
            </PieChartPlaceholder>
          ) : (
            <PieChart
              data={ data.chart }
              viewBoxWidth={ this.state.columnWidthPx }
              mainColor={ data.mainColor }
              mainFontColor={ data.mainFontColor }
              onSectorHover={ highlightCategory }
              onSectorClick={ (categoryId) => this.handleCategoryClick(categoryId as CategoryId | null, true) }
            />
          ) }
        </section>
      </section>
    )
  }

  renderPiesMobile () {
    const {
      member, peers, others,
      selectedPeergroupName,
      withComparison,
    } = this.props
    const { selected } = this.state

    return (
      <>
        <Carousel
          isFinite
          slideCount={ withComparison ? PIES_COUNT : 1 }
          activeIndex={ this.state.activeIndex }
          onIndexChange={ this.handleIndexChange }
          className={ styles.carousel }
        >
          { this.renderPie(member, 'your') }
          { withComparison ? this.renderPie(peers, 'peers') : null }
          { withComparison ? this.renderPie(others, 'other') : null }
        </Carousel>
        { withComparison && (
          <div className={ styles.groupNames }>
            <div
              className={ cn(styles.groupName, styles.member, {
                [styles.active]: selected === 0,
              }) }
              onClick={ this.handleGroupNameClick.bind(this, 0) }
            >
              You
            </div>
            <div
              className={ cn(styles.groupName, styles.peers, {
                [styles.active]: selected === 1,
              }) }
              onClick={ this.handleGroupNameClick.bind(this, 1) }
            >
              Your Peers
            </div>
            <div
              className={ cn(styles.groupName, styles.others, {
                [styles.active]: selected === 2,
              }) }
              onClick={ this.handleGroupNameClick.bind(this, 2) }
            >
              { selectedPeergroupName }
            </div>
          </div>
        )}
      </>
    )
  }

  renderPies () {
    const {
      selectedPeergroupName,
      member, peers, others,
      isMobile,
      withComparison,
    } = this.props

    if (!selectedPeergroupName) {
      return null
    }

    return isMobile
      ? this.renderPiesMobile()
      : (
        <>
          <div className={ styles.row }>
            { this.renderTitle(member.total, 'Your', styles.member, 'your') }
            { withComparison && this.renderTitle(peers.total, 'Peers', styles.peers, 'peers') }
            { withComparison && this.renderTitle(others.total, selectedPeergroupName, styles.others, 'other') }
          </div>
          <div className={ styles.row }>
            { this.renderPie(member, 'your') }
            { withComparison && this.renderPie(peers, 'peers') }
            { withComparison && this.renderPie(others, 'other') }
          </div>
        </>
      )
  }

  renderListsMobile () {
    if (!this.props.withList) {
      return null
    }

    const {
      member,
      peers,
      others,
      isExpanded,
      selectedId,
      withComparison,
    } = this.props

    const start = this.isMobileAndSpecificCategorySelected
      ? member.list.items.findIndex(({ id }) => id === selectedId)
      : 0
    let end: number | undefined = SHOWN_CATEGORIES_COUNT_MOBILE - 1
    if (isExpanded) {
      end = undefined
    }
    if (this.isMobileAndSpecificCategorySelected) {
      end = start + 1
    }

    return (
      <CategoriesListMobile
        itemsMember={ concat(this.getTotalItems(member), member.list.items.slice(start, end)) }
        itemsPeers={ concat(this.getTotalItems(peers), peers.list.items.slice(start, end)) }
        itemsOthers={ concat(this.getTotalItems(others), others.list.items.slice(start, end)) }
        onClickItem={ (categoryId) => this.handleCategoryClick(categoryId, false) }
        withComparison={ withComparison }
      />
    )
  }

  componentDidMount () {
    this.setColumnWidth()
    window.addEventListener('resize', this.handleResize)
  }

  componentWillUnmount () {
    this.handleResize.cancel()
    window.removeEventListener('resize', this.handleResize)
  }

  private calcColumnWidth () {
    const { isMobile } = this.props

    if (!this.moduleNode || isMobile) {
      return INITIAL_COLUMN_SIZE_PX
    }

    const style = getComputedStyle(this.moduleNode)
    const effectiveWidth = this.moduleNode.clientWidth - parseFloat(style.paddingLeft || '') - parseFloat(style.paddingRight || '')
    const count = isMobile ? PIES_COUNT_MOBILE : PIES_COUNT
    const columnWidth = Math.floor(effectiveWidth / count)

    // prevent viewbox size from growing - that leads to svg scaling and text becomes too small
    return Math.min(columnWidth, INITIAL_COLUMN_SIZE_PX)
  }

  private getTotalItems (item: CategoriesGroupWithList): CategoryItem[] {
    if (!this.props.withList) {
      return []
    }

    const { type } = this.props

    return [{
      id: ALL_CATEGORY_ID,
      categoryName: `Total ${ type }`,
      value: formatUSCurrency(item.total),
      isSelected: false,
      isShaded: false,
    }]
  }

  private setColumnWidth = () => {
    if (!this.moduleNode) {
      return
    }

    const columnWidthPx = this.calcColumnWidth()

    this.setState({
      isShown: true,
      columnWidthPx,
    })
  }

  private handleCategoryClick (category: string | null, isPie: boolean) {
    const { setFilteredCategory, trackUIEvent } = this.props
    const selectedId = this.props.withList ? this.props.selectedId : 'None'

    if (setFilteredCategory) {
      setFilteredCategory(category)
    }

    const label = `${ selectedId } → ${ category }`

    if (isPie) {
      trackUIEvent({
        category: 'module-spending_categories',
        action: 'select_category-from_pie',
        label,
      })
    } else {
      trackUIEvent({
        category: 'module-spending_categories',
        action: 'select_category-from_table',
        label,
      })

    }
  }

  private handleResize = debounce(this.setColumnWidth, 50)

  private handleButtonClick = () => {
    const { setExpanded } = this.props

    if (!setExpanded) {
      return
    }

    setExpanded(!this.isExpanded)
  }

  private handleIndexChange = (activeIndex: number, realIndex: number) => {
    this.setState({ activeIndex, selected: realIndex })
  }

  private handleGroupNameClick (selected: number) {
    const activeIndex = Math.floor(this.state.activeIndex / PIES_COUNT) * PIES_COUNT + selected
    this.setState({ selected, activeIndex })
  }
}

export default CategoriesModule
